import React from "react";
import { Alert } from "reactstrap";
import moment from "moment";
import { formatAmountRounded } from "../../helpers/currencyConversion";

export const RATE_RULE_CLOSING = "closing_rate";

const shortDate = (dateKey) => (dateKey ? moment(dateKey).format("DD/MM") : "");

/**
 * Por qué un pago se convierte con la tasa que se convierte (la `rate_rule`
 * que devuelve el backend): la tasa congelada al cierre del mes, si el pago
 * cae en el periodo de gracia, o la del día del pago.
 *
 * Solo dice algo cuando el condominio congela la tasa; con la regla apagada
 * o antes de que cierre el mes no hay nada que aclarar.
 */
const RateRuleNotice = ({ rule, className = "mt-2 mb-0 py-2 fs--1" }) => {
  if (!rule) return null;

  // Si el día de corte no tuvo publicación (fin de semana, feriado) la tasa
  // es de la siguiente fecha publicada: se dice para que el número cuadre con
  // el que ve el propietario en el BCV.
  const publishedOn =
    rule.rate_date && rule.rate_date !== rule.closing_date
      ? ` Publicada el ${shortDate(rule.rate_date)}.`
      : "";

  switch (rule.reason) {
    case "in_grace":
      return (
        <Alert color="success" className={className}>
          Se está aplicando la tasa congelada al cierre de mes (
          {shortDate(rule.closing_date)}): <strong>{formatAmountRounded(rule.rate, 2)}</strong>.
          Válida hasta el {shortDate(rule.grace_until)}.{publishedOn}
        </Alert>
      );
    case "grace_expired":
      return (
        <Alert color="danger" className={className}>
          Periodo de tasa congelada vencido (era válida hasta el{" "}
          {shortDate(rule.grace_until)}). Se aplica la tasa del día del pago.
        </Alert>
      );
    case "mixed_periods":
      return (
        <Alert color="warning" className={className}>
          Cada planilla se paga con su tasa: la que está en su periodo de
          gracia, con la tasa congelada al cierre de mes (
          {shortDate(rule.closing_date)}), válida hasta el{" "}
          {shortDate(rule.grace_until)}; las demás, con la tasa del día del
          pago.
        </Alert>
      );
    case "closing_rate_unavailable":
      return (
        <Alert color="warning" className={className}>
          No hay tasa registrada para el cierre de mes (
          {shortDate(rule.closing_date)}). Se aplica la tasa del día del pago.
        </Alert>
      );
    default:
      return null;
  }
};

/**
 * Versión de una línea para el detalle de un pago ya reportado: qué tasa se
 * aplicó y por qué.
 */
export const rateRuleSummary = (rule) => {
  if (!rule || rule.rate === null || rule.rate === undefined) return null;

  const rate = formatAmountRounded(rule.rate, 2);

  if (rule.type === RATE_RULE_CLOSING) {
    return `${rate} · Tasa congelada al cierre de mes (${shortDate(rule.closing_date)}), periodo de gracia hasta el ${shortDate(rule.grace_until)}`;
  }

  if (rule.reason === "mixed_periods") {
    return `${rate} · Varias planillas: la de su periodo de gracia a la tasa congelada al cierre de mes (${shortDate(rule.closing_date)}), las demás a la del día del pago (${shortDate(rule.payment_date)})`;
  }

  const reason =
    rule.reason === "grace_expired"
      ? ` · Periodo de tasa congelada vencido el ${shortDate(rule.grace_until)}`
      : "";

  return `${rate} · Tasa del día del pago (${shortDate(rule.payment_date)})${reason}`;
};

export default RateRuleNotice;
